import React from "react";
import { TransformationContext, Transformation, defaultTransformation } from "./TransformationContext";

import { useRenderTarget, RenderTarget } from "./renderTarget";
import { isUiModeDeveloper } from "../../uiMode";
import PdfListUtils from "../PdfLists/general/PdfListUtils";
import { createPDFStyleSheet } from "./cssHelpers";
import { measureTextForPDF } from "../../modelViews/GraficElementUtils";
import { getPdfRenderer } from "../../getPdfRenderer";

export type RTextProps = {
  style?: React.CSSProperties;
  children: string | string[];
};

export const RText = React.forwardRef<HTMLSpanElement, RTextProps>((props, ref) => {
  const target = useRenderTarget();
  const parentTransformation = React.useContext(TransformationContext);

  let { style, children } = props;

  // Originales CSS-Style sichern, bevor es für PDF konvertiert wird
  const originalStyle: React.CSSProperties = { ...(style || {}) };
  let textContent = ""
  if (Array.isArray(children)) {
    textContent = children.join("");
  } else {
    textContent = String(children || "");
  }

  if (target === RenderTarget.web) {
    return (
      <span ref={ref} style={style}>
        {textContent}
      </span>
    );
  }

  // Für PDF: Vereinfachte Text-Komponente nach CellText Muster
  style ||= {};

  PdfListUtils.convertStyleUnitsForPDF(style, "RText"); // no itx
  PdfListUtils.applyTransformation(style, parentTransformation);

  let pdfStyle = createPDFStyleSheet(style);

  // Nur die wichtigsten Style-Properties, ähnlich wie CellText
  pdfStyle.fontSize = style.fontSize || 12;



  // Debug-Ausgabe für Text-Content
  if (isUiModeDeveloper()) {
    console.log("RText PDF rendering:", textContent, "with style:", style);
  }

  // Textbreite berechnen für whiteSpace: nowrap Ersatz
  let calculatedCssWidth = 20;
  let calculatedPdfWidth = 0;

  if (!style.whiteSpace || style.whiteSpace === "nowrap") {
    try {
      // Font-Size aus dem originalen CSS-Style verwenden (in px), nicht aus dem bereits konvertierten PDF-Style
      let cssFontSize: string;
      if (typeof originalStyle.fontSize === "number") {
        cssFontSize = `${originalStyle.fontSize}px`;
      } else if (typeof originalStyle.fontSize === "string" && originalStyle.fontSize.trim().length > 0) {
        cssFontSize = originalStyle.fontSize;
      } else {
        cssFontSize = "14px"; // Fallback
      }

      const pdfFontSize = PdfListUtils.convertToPDFPoints(cssFontSize);


      // Verwende PDFKit-Messung (gleiche Metriken wie React-PDF)
      calculatedPdfWidth = measureTextForPDF(textContent, cssFontSize, {
        fontFamily: originalStyle.fontFamily as string | undefined,
        fontWeight: originalStyle.fontWeight,
        fontStyle: originalStyle.fontStyle as string | undefined,
      }).width;


      if (isUiModeDeveloper()) {
        console.log(
          `RText width calculation: "${textContent}" | FontSize: ${pdfFontSize} | Final: ${calculatedCssWidth}px`,
        );
      }
    } catch (error) {
      console.error(error);
    }
  }

  const pdf = getPdfRenderer();
  if (!pdf) return null;

  const { Text, View } = pdf;

  // Container-Breite aus Style (falls gesetzt) für clippende View
  const rawWidth = style.width;
  const containerWidthPdf =
    rawWidth !== undefined && rawWidth !== null
      ? typeof rawWidth === "number"
        ? rawWidth
        : PdfListUtils.convertToPDFPoints(String(rawWidth))
      : undefined;

  const pdfWidth = calculatedPdfWidth;



  if (!pdfStyle.width) {
    pdfStyle.width = pdfWidth;
  }

  // PDF-StyleSheet mit StyleSheet.create erstellen (wie RDiv)
  let cleanedPDFStyle = createPDFStyleSheet(pdfStyle);
  cleanedPDFStyle.width = undefined;

    console.log("RDiv: ", textContent, cleanedPDFStyle.fontSize);

  const textElement = (
    <Text style={cleanedPDFStyle} wrap={false}>
      {textContent}
    </Text>);
  return textElement;
});

RText.displayName = "RText";
